import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, InfoIcon, RocketIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Meter } from '../../components/ui/Meter';
import { Modal } from '../../components/ui/Modal';
import { Field, Textarea } from '../../components/ui/Field';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { ScopeSwitcher } from '../../components/admin/ScopeSwitcher';
import { useGovernance } from '../../contexts/GovernanceContext';
import { allCampuses, evaluateLaunch, scopeCovers } from '../../utils/governance';
import type { CampusOperationalStatus, CheckSeverity } from '../../types/governance';
const STATUS_LABEL: Record<CampusOperationalStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  ready_for_review: 'Ready for review',
  approved: 'Approved',
  live: 'Live',
  suspended: 'Suspended'
};
const SEVERITY: Record<CheckSeverity, {
  label: string;
  tone: 'danger' | 'warn' | 'neutral';
}> = {
  blocker: {
    label: 'Blocker',
    tone: 'danger'
  },
  warning: {
    label: 'Warning',
    tone: 'warn'
  },
  info: {
    label: 'Info',
    tone: 'neutral'
  }
};

/**
 * Campus launch control. The checklist is the gate, not a report: a campus
 * cannot be activated while a mandatory check is outstanding, and the engine
 * derives that from the checks rather than trusting a stored "ready" flag.
 */
export function CampusLaunch() {
  const {
    tenants,
    launchChecks,
    activeScope,
    scopeLabel
  } = useGovernance();
  const [requesting, setRequesting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const outcomes = useMemo(() => allCampuses(tenants).filter(({
    tenant,
    campus
  }) => {
    const institution = tenant.institutions.find(item => item.campuses.some(entry => entry.id === campus.id));
    return scopeCovers(activeScope, {
      level: 'campus',
      tenantId: tenant.id,
      institutionId: institution?.id,
      campusId: campus.id
    });
  }).map(entry => ({
    ...entry,
    outcome: evaluateLaunch(entry.campus, launchChecks)
  })), [activeScope, launchChecks, tenants]);
  const target = outcomes.find(entry => entry.campus.id === requesting) ?? null;
  return <div className="space-y-6" data-id="element-5797" data-mp="1qu65l2g1uovj">
      <PageHeader eyebrow="Administration" title="Campus launch control" description="Every campus that exists, and exactly what is outstanding before it can operate. Adding a campus needs configuration, not code." actions={<Badge plain data-id="element-5799" data-mp="1hvkshmtck225">{scopeLabel(activeScope)}</Badge>} data-id="element-5798" data-mp="1quxxo3afu4h2" />

      <Panel title="Operating context" data-id="element-5800" data-mp="ah7vx36sjsal">
        <ScopeSwitcher data-id="element-5801" data-mp="6e2uz52czyo2" />
      </Panel>

      {outcomes.map(({
      campus,
      tenant,
      institution,
      outcome
    }) => <Panel key={campus.id} title={`${institution} — ${campus.name}`} description={`${tenant.name} · ${campus.city} · ${campus.timezone}`} action={<Badge tone={campus.status === 'live' ? 'ok' : campus.status === 'suspended' ? 'danger' : campus.status === 'not_started' ? 'neutral' : 'warn'} data-id="element-5803" data-mp="2r231svoydl09">
              {STATUS_LABEL[campus.status]}
            </Badge>} data-id="element-5802" data-mp="2i1oiu2zgohzx">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)]" data-id="element-5804" data-mp="39scgv65u979d">
            <div data-id="element-5805" data-mp="3erfa7tcghp1i">
              <p className="text-body-sm text-ink-muted" data-id="element-5806" data-mp="3erxgzjwqvlno">Checks satisfied</p>
              <p className="cz-num text-h2 font-semibold text-ink" data-id="element-5807" data-mp="1fetdhfp4mug5">
                {outcome.satisfied}
                <span className="text-h4 font-normal text-ink-muted" data-id="element-5808" data-mp="1kd4lrlzrwww9"> / {outcome.total}</span>
              </p>
              <div className="mt-2" data-id="element-5809" data-mp="p2ommiuzm40a">
                <Meter value={outcome.satisfied / outcome.total * 100} label="Launch readiness" tone={outcome.canGoLive ? 'ok' : 'warn'} data-id="element-5810" data-mp="p1mxxbl1tj03" />
              </div>
              <p className="mt-3 text-body-sm text-ink" data-id="element-5811" data-mp="u2kpufvv1res">
                {campus.status === 'live' ? 'This campus is operating. Outstanding warnings still need closing.' : outcome.canGoLive ? 'No blockers outstanding. Activation can be requested.' : `${outcome.blockers.length} blocker${outcome.blockers.length === 1 ? '' : 's'} must be cleared before activation.`}
              </p>
              {campus.status !== 'live' && <div className="mt-4" data-id="element-5812" data-mp="3jrmpz97sec7y">
                  <PermissionGate permission="campuses.launch" scope={{
              level: 'campus',
              tenantId: tenant.id,
              campusId: campus.id
            }} label="Requesting campus activation" data-id="element-5813" data-mp="3jqlej3muznpp">
                    <Button variant="primary" size="sm" icon={<RocketIcon className="h-4 w-4" data-id="element-5815" data-mp="1kaqm3rxfpgc1" />} disabled={!outcome.canGoLive} onClick={() => setRequesting(campus.id)} data-id="element-5814" data-mp="1z3ajebxwktlk">
                      Request activation
                    </Button>
                    {!outcome.canGoLive && <p className="mt-2 text-body-sm text-warn-700" data-id="element-5816" data-mp="240keo87z3zhb">
                        Activation is blocked while a mandatory check is outstanding. This is not a
                        warning you can dismiss.
                      </p>}
                  </PermissionGate>
                </div>}
            </div>

            <ul className="space-y-2" data-id="element-5817" data-mp="wok0u3z0243v">
              {launchChecks.map(check => {
            const satisfied = check.satisfiedFor.includes(campus.id);
            const severity = SEVERITY[check.severity];
            return <li key={check.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5 rounded-md border border-line px-3 py-2.5" data-id="element-5818" data-mp="2pjl03wnfr4hq">
                    <div className="flex min-w-0 items-start gap-2.5" data-id="element-5819" data-mp="2gjpl1gqrepcf">
                      {satisfied ? <CheckCircle2Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-5820" data-mp="2bjtou3ob027f" /> : check.severity === 'blocker' ? <AlertTriangleIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-danger-700" data-id="element-5821" data-mp="21nqdc77kar7t" /> : <InfoIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-5822" data-mp="3vy3t6mvama7f" />}
                      <div className="min-w-0" data-id="element-5823" data-mp="ccwk3rml8o62">
                        <p className="text-body font-medium text-ink" data-id="element-5824" data-mp="cblgpsx21ifh">{check.label}</p>
                        {!satisfied && <p className="mt-0.5 max-w-[70ch] text-body-sm text-ink-muted" data-id="element-5825" data-mp="h67h711llqks">
                            {check.requirement}
                          </p>}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-5826" data-mp="3pkpt5duntq9o">
                      <Badge plain data-id="element-5827" data-mp="3ui83n8obsdjs">{check.domain}</Badge>
                      {satisfied ? <Badge tone="ok" data-id="element-5828" data-mp="3cznn3tyil0g">Satisfied</Badge> : <Badge tone={severity.tone} data-id="element-5829" data-mp="19sioeujpmpqk">{severity.label}</Badge>}
                    </div>
                  </li>;
          })}
            </ul>
          </div>
        </Panel>)}

      <Modal open={target !== null} onClose={() => setRequesting(null)} title={target ? `Activate ${target.campus.name}` : 'Activate campus'} description="Activation is an approval, not a switch. A second authorised person must release it." footer={<>
            <Button variant="secondary" onClick={() => setRequesting(null)} data-id="element-5831" data-mp="24tz04grev5f4">
              Cancel
            </Button>
            <Button variant="primary" disabled={!reason.trim()} onClick={() => {
        setRequesting(null);
        setReason('');
      }} data-id="element-5832" data-mp="2jhtue1l2hhub">
              Submit for approval
            </Button>
          </>} data-id="element-5830" data-mp="1zwpfrfivpjy5">
        {target && <div className="space-y-4" data-id="element-5833" data-mp="2njfbjz4mwiab">
            <p className="text-body-sm text-ink" data-id="element-5834" data-mp="y5iq2vep49w8">
              {target.outcome.warnings.length > 0 ? `${target.outcome.warnings.length} warning${target.outcome.warnings.length === 1 ? '' : 's'} will remain outstanding after activation. They do not block launch, but they stay on the record until closed.` : 'No warnings outstanding.'}
            </p>
            <Field label="Why this campus is ready" htmlFor="launch-reason" data-id="element-5835" data-mp="2w2sgrg0cw4a3">
              <Textarea id="launch-reason" rows={3} value={reason} onChange={event => setReason(event.target.value)} placeholder="Recorded on the approval request and the audit log" data-id="element-5836" data-mp="303uji6theygt" />
            </Field>
          </div>}
      </Modal>
    </div>;
}